import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { updateMeSchema, type NotificationType } from '@wrapforce/shared';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useMe, useUpdateMe } from '@/features/session/api';
import { useUpdateWorkspace, useWorkspaceDetail } from '@/features/workspace/api';
import { useThemeStore, type ThemePref } from '@/stores/theme';
import { useLocaleStore } from '@/stores/locale';
import { SUPPORTED_LOCALES } from '@/lib/i18n';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { authErrorMessage } from '@/lib/api-errors';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, FieldError } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { QuoteDefaultsSettings } from './QuoteDefaultsSettings';
import { BillingSettings } from './BillingSettings';
import { BaysSettings } from './BaysSettings';
import { ShopHoursSettings } from './ShopHoursSettings';

type MeFormValues = z.input<typeof updateMeSchema>;

const TABS = [
  { key: 'profile', labelKey: 'tabs.profile', roles: ['owner', 'manager', 'front_desk', 'installer'] },
  { key: 'appearance', labelKey: 'tabs.appearance', roles: ['owner', 'manager', 'front_desk', 'installer'] },
  { key: 'quote-defaults', labelKey: 'tabs.quoteDefaults', roles: ['owner', 'manager'] },
  { key: 'bays', labelKey: 'tabs.bays', roles: ['owner', 'manager'] },
  { key: 'shop-hours', labelKey: 'tabs.shopHours', roles: ['owner'] },
  { key: 'billing', labelKey: 'tabs.billing', roles: ['owner'] },
  { key: 'notifications', labelKey: 'tabs.notifications', roles: ['owner'] },
  { key: 'security', labelKey: 'tabs.security', roles: ['owner', 'manager', 'front_desk', 'installer'] },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function SettingsPage() {
  const { t } = useTranslation('settings');
  const { membership } = useActiveMembership();
  const role = membership?.role ?? 'installer';
  const workspaceId = membership?.workspaceId ?? null;
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = (TABS.find((t) => t.key === tabParam)?.key ?? 'profile') as TabKey;
  const [tab, setTab] = useState<TabKey>(initialTab);

  const visibleTabs = TABS.filter((t) => (t.roles as readonly string[]).includes(role));
  // role falls back to 'installer' while membership is loading, so the tab picked from
  // the URL/state may not be in visibleTabs yet - re-derive at render time so an owner
  // deep-linking to ?tab=billing lands there once membership finishes loading.
  const effectiveTab = visibleTabs.some((tb) => tb.key === tab) ? tab : 'profile';

  return (
    <div className="flex flex-col gap-4 p-6 md:flex-row md:gap-5">
      <nav className="-mx-6 flex gap-0.5 overflow-x-auto px-6 md:mx-0 md:w-52 md:flex-none md:flex-col md:px-0" data-tour="page-settings-nav">
        {visibleTabs.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={cn(
              'min-h-safe-tap flex-none whitespace-nowrap px-3 py-2 text-left text-sm font-medium md:min-h-0',
              effectiveTab === tb.key ? 'bg-ink text-surface' : 'text-muted hover:bg-surface-2',
            )}
          >
            {t(tb.labelKey)}
          </button>
        ))}
      </nav>

      <div className="max-w-2xl flex-1">
        {effectiveTab === 'profile' ? <ProfileTab /> : null}
        {effectiveTab === 'appearance' ? <AppearanceTab /> : null}
        {effectiveTab === 'quote-defaults' ? <QuoteDefaultsSettings workspaceId={workspaceId} /> : null}
        {effectiveTab === 'bays' ? <BaysSettings workspaceId={workspaceId} /> : null}
        {effectiveTab === 'shop-hours' ? <ShopHoursSettings workspaceId={workspaceId} /> : null}
        {effectiveTab === 'billing' ? <BillingSettings workspaceId={workspaceId} /> : null}
        {effectiveTab === 'notifications' ? <NotificationsTab workspaceId={workspaceId} /> : null}
        {effectiveTab === 'security' ? <SecurityTab /> : null}
      </div>
    </div>
  );
}

function ProfileTab() {
  const { t } = useTranslation('settings');
  const { data: me } = useMe();
  const updateMe = useUpdateMe();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MeFormValues>({
    resolver: zodResolver(updateMeSchema),
    values: { fullName: me?.fullName ?? '', phone: me?.phone ?? '' },
  });

  async function onSubmit(values: MeFormValues) {
    await updateMe.mutateAsync(updateMeSchema.parse(values));
    toast.success(t('toast.profileUpdated'));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('profile.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fullName">{t('profile.fullName')}</Label>
            <Input id="fullName" {...register('fullName')} />
            <FieldError>{errors.fullName?.message}</FieldError>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="phone">{t('profile.phone')}</Label>
            <Input id="phone" {...register('phone')} placeholder={t('profile.phonePlaceholder')} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">{t('profile.email')}</Label>
            <Input id="email" value={me?.email ?? ''} disabled />
          </div>
          <Button type="submit" loading={updateMe.isPending} className="w-fit">
            {t('common:actions.save')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function AppearanceTab() {
  const { t } = useTranslation();
  const { pref, setPref } = useThemeStore();
  const { locale, setLocale } = useLocaleStore();
  const { data: me } = useMe();
  const updateMe = useUpdateMe();
  const options: { value: ThemePref; labelKey: string }[] = [
    { value: 'system', labelKey: 'appearance.system' },
    { value: 'light', labelKey: 'appearance.light' },
    { value: 'dark', labelKey: 'appearance.dark' },
  ];
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('appearance.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-3">
          {options.map((o) => (
            <button
              key={o.value}
              onClick={() => setPref(o.value)}
              className={cn('flex flex-1 flex-col items-center gap-2 rounded-[var(--radius-card)] border p-4 text-sm font-semibold', pref === o.value ? 'border-accent text-accent-ink' : 'border-border text-muted')}
            >
              <span
                className={cn(
                  'h-10 w-16  border border-border',
                  o.value === 'dark' ? 'bg-[#0B0C0E]' : o.value === 'light' ? 'bg-white' : 'bg-gradient-to-r from-white to-[#0B0C0E]',
                )}
              />
              {t(o.labelKey)}
            </button>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t('topbar.language')}</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-3">
          {SUPPORTED_LOCALES.map((l) => (
            <button
              key={l}
              onClick={() => {
                setLocale(l);
                if (me) updateMe.mutate({ locale: l });
              }}
              className={cn('flex-1 rounded-[var(--radius-card)] border p-4 text-sm font-semibold', l === locale ? 'border-accent text-accent-ink' : 'border-border text-muted')}
            >
              {t(`language.${l}`)}
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

const NOTIFICATION_GROUPS: { labelKey: string; events: NotificationType[] }[] = [
  { labelKey: 'notifications.groups.sales', events: ['lead_created', 'quote_viewed', 'deposit_paid', 'message_received'] },
  { labelKey: 'notifications.groups.team', events: ['job_assigned', 'invite_accepted'] },
  { labelKey: 'notifications.groups.operations', events: ['inventory_low'] },
  { labelKey: 'notifications.groups.billing', events: ['credits_low', 'plan_changed', 'trial_expiring', 'stripe_action_required'] },
];

const EVENT_LABEL_KEYS: Record<NotificationType, string> = {
  lead_created: 'notifications.events.leadCreated',
  quote_viewed: 'notifications.events.quoteViewed',
  deposit_paid: 'notifications.events.depositPaid',
  message_received: 'notifications.events.messageReceived',
  job_assigned: 'notifications.events.jobAssigned',
  invite_accepted: 'notifications.events.inviteAccepted',
  inventory_low: 'notifications.events.inventoryLow',
  credits_low: 'notifications.events.creditsLow',
  plan_changed: 'notifications.events.planChanged',
  trial_expiring: 'notifications.events.trialExpiring',
  stripe_action_required: 'notifications.events.stripeActionRequired',
};

function NotificationsTab({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('settings');
  const { data: detail } = useWorkspaceDetail(workspaceId);
  const updateWorkspace = useUpdateWorkspace(workspaceId);
  const settings = (detail?.settings ?? {}) as {
    notifyDepositPaid?: boolean;
    notifyFirstView?: boolean;
    notificationPrefs?: Partial<Record<NotificationType, boolean>>;
  };
  const prefs = settings.notificationPrefs;

  function isEnabled(type: NotificationType): boolean {
    if (prefs) return prefs[type] !== false;
    if (type === 'deposit_paid' && settings.notifyDepositPaid === false) return false;
    if (type === 'quote_viewed' && settings.notifyFirstView === false) return false;
    return true;
  }

  function toggle(type: NotificationType, value: boolean) {
    updateWorkspace.mutate({ settings: { ...settings, notificationPrefs: { ...prefs, [type]: value } } });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('notifications.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {NOTIFICATION_GROUPS.map((group) => (
          <div key={group.labelKey} className="flex flex-col gap-3">
            <span className="text-xs font-bold uppercase tracking-wide text-muted">{t(group.labelKey)}</span>
            {group.events.map((type) => (
              <ToggleRow key={type} label={t(EVENT_LABEL_KEYS[type])} checked={isEnabled(type)} onChange={(v) => toggle(type, v)} />
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4">
      <span className="text-sm text-ink">{label}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[var(--color-accent)]" />
    </label>
  );
}

function SecurityTab() {
  const { t } = useTranslation('settings');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleChangePassword() {
    if (newPassword.length < 8) {
      toast.error(t('toast.passwordTooShort'));
      return;
    }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setSaving(false);
    if (error) {
      toast.error(t('toast.passwordUpdateFailed'), { description: authErrorMessage(error, t('errors.fallback.body', { ns: 'common' })) });
      return;
    }
    setNewPassword('');
    toast.success(t('toast.passwordUpdated'));
  }

  async function handleSignOutEverywhere() {
    await supabase.auth.signOut({ scope: 'global' });
    window.location.assign('/login');
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('security.changePasswordTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('security.newPasswordPlaceholder')} />
          <Button onClick={handleChangePassword} loading={saving} className="w-fit">
            {t('security.updatePassword')}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t('security.sessionsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted">{t('security.signOutDescription')}</p>
          <Button variant="outline" onClick={handleSignOutEverywhere}>
            {t('security.signOutEverywhere')}
          </Button>
        </CardContent>
      </Card>
      <p className="text-xs text-muted">{t('security.inviteNote')}</p>
    </div>
  );
}
